import {
  getIsotopes,
  removeIsotope,
  setIsotopeFilters,
  updateIsotope,
} from '@state/reducers/isotopeReducer'
import { GridColumns } from '@mui/x-data-grid-pro'
import { IsotopeDto, IsotopeUsage } from '@services/dtos'
import { useEffect, useMemo } from 'react'
import BaseConfigTable from './BaseConfigTable'
import IsotopeForm from '../forms/IsotopeForm'
import { AppDispatch } from '@/state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { ActivityLevelLabels, IsotopeEmissionTypeShortLabels } from '@utils/constants'
import { humanizeDurationSeconds } from '@utils/date.utils'

export default function IsotopesTable() {
  const dispatch = useAppDispatch()
  useEffect(() => {
    return () => {
      dispatch(setIsotopeFilters({ includeInactive: false }))
    }
  }, [])
  
  const columns: GridColumns<IsotopeDto> = useMemo(
    () => [
      { field: 'code', headerName: 'Code', width: 70 },
      { field: 'name', headerName: 'Label', width: 120 },
      { field: 'atomicNumber', headerName: 'NA (Numéro Atomique)', width: 40 },
      {
        field: 'physicalHalfLifeS',
        headerName: 'Demi-vie', 
        width: 170,
        valueGetter: (params) => {
          const s = params.row.physicalHalfLifeS
          const formatted = humanizeDurationSeconds(s)
          return `${s}s${formatted ? ` (${formatted})` : ''}`
        }
      },
      {
        field: 'emissions',
        headerName: 'Émissions',
        width: 90,
        valueGetter: (params) => params.row.emissions?.map(
          emission => IsotopeEmissionTypeShortLabels[emission]).join(', '),
      },
      {
        field: 'activityLevel',
        headerName: 'Activité',
        width: 75,
        valueGetter: (params) => ActivityLevelLabels[params.row.activityLevel]
      },
      {
        field: 'meanEnergyKev',
        headerName: 'Énergie moyenne (keV)',
        width: 105,
      },
      {
        field: 'peakEnergyKev',
        headerName: 'Pic énergie (keV)',
        width: 100,
      },
      { field: 'treatment', headerName: 'Traitement', type: 'boolean', width: 100, valueGetter: (params) => 
        params.row.usages?.includes(IsotopeUsage.TREATMENT) ?? false },
      { field: 'diagnostic', headerName: 'Diagnostic', type: 'boolean', width: 100, valueGetter: (params) => 
        params.row.usages?.includes(IsotopeUsage.DIAGNOSTIC) ?? false },
      { field: 'notes', headerName: 'Notes', width: 500 },
    ],
    [],
  )

  return (
    <BaseConfigTable<IsotopeDto>
      columns={columns}
      selectRows={(state) => state.isotope.items}
      fetchThunk={() => async (dispatch: AppDispatch) => {
        dispatch(setIsotopeFilters({ includeInactive: true }))
        return dispatch(getIsotopes())
      }}
      deleteThunk={removeIsotope}
      toggleThunk={(id, isActive) => updateIsotope({ id, dto: { isActive } })}
      updatePageThunk={(page) => async (dispatch: AppDispatch) => {
        dispatch(setIsotopeFilters({ page }))
        dispatch(getIsotopes())
      }}
      FormComponent={({ editingItem, onClose }) => (
        <IsotopeForm editingIsotope={editingItem} onClose={onClose} />
      )}
      resourceLabel="isotope"
      resourcePrefix="l'"
    />
  )
}
